import { useState, useEffect, useRef } from 'react';
import { logger } from '@/utils/logger';

/**
 * Custom hook to manage auth ready state with fallback timeout
 * Prevents infinite loading on iOS/slow networks by ensuring isAuthReady always resolves
 */
export const useAuthReady = (loadingSession: boolean) => {
  const [isAuthReady, setIsAuthReady] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If session is already loaded, set ready immediately
    if (!loadingSession) {
      logger.log('[useAuthReady] Session loaded, setting auth ready');
      setIsAuthReady(true);
      return;
    }

    // Set fallback timeout for stuck session loading
    timeoutRef.current = setTimeout(() => {
      logger.warn('[useAuthReady] ⏰ Fallback timeout reached - setting isAuthReady true after 10s');
      setIsAuthReady(true);
      timeoutRef.current = null;
    }, 10000);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [loadingSession]);

  return isAuthReady;
};